import dayjs from 'dayjs';
import { useState } from 'react';

import { BadgePadding } from 'types';
import { formatDate } from 'utils/date';
import { TemplateList } from 'components/transactions-list';
import { Refund, useLazyDetailsQuery } from 'store/api/refund';
import { formatPrice, getCommissionsAmount } from 'utils/helpers';
import { RefundStatusBadge } from 'components/refund-status-badge';
import { useShowMore, handleMutation, useFetchByQueryId } from 'hooks';

import { RefundDetails } from './details';
import { RefundsListProps } from './types';

export const refundsConfig = {
  template: '1fr 2fr repeat(6, 1fr)',
  cellsMobile: [
    { row: '2/3', col: '1/2' },
    { row: '1/2', col: '1/3' },
    { row: '2/3', col: '2/3' },
  ],
  headers: [
    'Date',
    'Event',
    'Sale Total',
    'Commissions',
    'Tip',
    'Refund Amount',
    'Refund Tip',
    'Status',
  ],
};

export const RefundsList = ({
  data,
  loadMoreProps,
  ...props
}: RefundsListProps) => {
  const [selectedRefund, setSelectedRefund] = useState<Refund>();
  const { visibleData, ...showMoreProps } = useShowMore(data);

  const [trigger, { ...detailsProps }] = useLazyDetailsQuery();
  handleMutation(detailsProps);

  const { setQueryId } = useFetchByQueryId<Refund>({
    trigger,
    queryName: 'refundId',
    selectDetails: setSelectedRefund,
  });

  const rows = loadMoreProps ? data : visibleData;

  return (
    <>
      <TemplateList<Refund>
        {...refundsConfig}
        title="Refunds"
        subtitle={
          loadMoreProps
            ? `${rows?.length ?? 0} of ${loadMoreProps.totalCount} shown`
            : undefined
        }
        data={rows}
        hasMinHeight={false}
        listProps={loadMoreProps ?? showMoreProps}
        onClick={(e, value) => setSelectedRefund(value)}
        maxHeight={rows?.length ? 350 : undefined}
        render={(refund) => {
          const timezoneDate = dayjs(refund.createdAt).tz(
            refund.checkout?.event?.timezone?.code
          );

          return [
            formatDate(timezoneDate),
            refund.checkout?.event?.name,
            formatPrice(refund.checkout?.saleTotal),
            formatPrice(getCommissionsAmount(refund?.checkout)),
            formatPrice(refund.checkout?.tip),
            formatPrice(refund?.totalRefundPayments),
            formatPrice(refund?.totalRefundTip),
            <RefundStatusBadge
              status={refund.status}
              padding={BadgePadding.Default}
            />,
          ];
        }}
      />

      <RefundDetails
        {...props}
        data={selectedRefund!}
        opened={!!selectedRefund}
        onClose={() => {
          setQueryId('');
          setSelectedRefund(undefined);
        }}
      />
    </>
  );
};
